
import { useState } from 'react';

export default function SupportMethod() {
  const [selectedMonthlyAmount, setSelectedMonthlyAmount] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState('');

  const monthlyAmounts = [
    { value: '10000', label: '1만원' },
    { value: '30000', label: '3만원' },
    { value: '50000', label: '5만원' },
    { value: '100000', label: '10만원' },
  ];

  const scrollToForm = () => {
    const formSection = document.getElementById('support-process');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="support-method" className="py-16 sm:py-20 md:py-28 bg-gradient-to-b from-white via-teal-50/30 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 bg-teal-100 text-teal-700 text-xs sm:text-sm font-semibold rounded-full mb-4">
            WAYS TO GIVE
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            후원 방법 선택
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            여러분의 소중한 후원이 선교 현장에 큰 힘이 됩니다
          </p>
        </div>

        {/* Cards Container */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10 max-w-6xl mx-auto">
          
          {/* Step 1: Monthly Support Card */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-shadow">
            {/* Card Header */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 sm:px-8 py-6 sm:py-8">
              <div className="flex items-center mb-3">
                <span className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full text-white text-sm sm:text-base font-bold mr-3">
                  1
                </span>
                <span className="text-white/80 text-xs sm:text-sm font-medium uppercase tracking-wider">
                  STEP 1 · Monthly Support
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/20 rounded-2xl flex items-center justify-center mr-4">
                  <i className="ri-calendar-check-line text-2xl sm:text-3xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">정기 후원</h3>
                  <p className="text-white/80 text-xs sm:text-sm mt-1">Monthly Support</p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6 sm:p-8">
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-6">
                매월 약정된 금액으로 킹덤브릿지의 사역이 멈추지 않게 하는 <strong className="text-teal-600">가장 큰 힘</strong>입니다.
              </p>

              {/* Amount Options */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <i className="ri-money-dollar-circle-line mr-1"></i>
                  금액 선택
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-3">
                  {monthlyAmounts.map((amount) => (
                    <button
                      key={amount.value}
                      onClick={() => {
                        setSelectedMonthlyAmount(amount.value);
                        setCustomAmount('');
                      }}
                      className={`px-3 sm:px-4 py-3 sm:py-4 rounded-xl font-semibold text-sm sm:text-base transition-all cursor-pointer whitespace-nowrap ${
                        selectedMonthlyAmount === amount.value
                          ? 'bg-teal-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-teal-50 hover:text-teal-600'
                      }`}
                    >
                      {amount.label}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedMonthlyAmount('custom');
                    }}
                    placeholder="기타 금액 직접 입력"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-base pr-12"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">원</span>
                </div>
              </div>

              {/* Quote */}
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4 sm:p-5 mb-6 border border-teal-100">
                <div className="flex items-start">
                  <i className="ri-double-quotes-l text-2xl text-teal-400 mr-2 flex-shrink-0"></i>
                  <p className="text-sm sm:text-base text-teal-800 font-medium italic leading-relaxed">
                    매달 기도의 무릎을 함께 모아주세요.
                  </p>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-2 sm:space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-double-line text-teal-600 text-lg mt-0.5 mr-2 flex-shrink-0"></i>
                  <span className="text-xs sm:text-sm text-gray-700">안정적이고 지속적인 사역 지원</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-double-line text-teal-600 text-lg mt-0.5 mr-2 flex-shrink-0"></i>
                  <span className="text-xs sm:text-sm text-gray-700">자동이체로 편리한 관리</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-double-line text-teal-600 text-lg mt-0.5 mr-2 flex-shrink-0"></i>
                  <span className="text-xs sm:text-sm text-gray-700">정기 소식지 및 기도편지 발송</span>
                </li>
              </ul>

              {/* CTA Button */}
              <button
                onClick={scrollToForm}
                className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white font-bold rounded-xl hover:from-teal-700 hover:to-teal-800 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap text-sm sm:text-base"
              >
                <i className="ri-arrow-down-line mr-2"></i>
                정기 후원 신청하기
              </button>
            </div>
          </div>

          {/* Step 2: One-time Gift Card */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-shadow">
            {/* Card Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 sm:px-8 py-6 sm:py-8">
              <div className="flex items-center mb-3">
                <span className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full text-white text-sm sm:text-base font-bold mr-3">
                  2
                </span>
                <span className="text-white/80 text-xs sm:text-sm font-medium uppercase tracking-wider">
                  STEP 2 · One-time Gift
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/20 rounded-2xl flex items-center justify-center mr-4">
                  <i className="ri-gift-line text-2xl sm:text-3xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">프로젝트 일시 후원</h3>
                  <p className="text-white/80 text-xs sm:text-sm mt-1">One-time Gift</p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6 sm:p-8">
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-6">
                선교지 탐방 영상 제작, 긴급 구호, 신학교 교재 발간 등 <strong className="text-amber-600">특별한 프로젝트</strong>를 지원합니다.
              </p>

              {/* Project Examples */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <i className="ri-folder-open-line mr-1"></i>
                  지원 가능한 프로젝트
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <div className="flex items-center p-3 bg-amber-50 rounded-xl border border-amber-100">
                    <i className="ri-video-line text-amber-600 text-lg mr-2"></i>
                    <span className="text-xs sm:text-sm text-gray-700">선교지 탐방 영상 제작</span>
                  </div>
                  <div className="flex items-center p-3 bg-amber-50 rounded-xl border border-amber-100">
                    <i className="ri-heart-pulse-line text-amber-600 text-lg mr-2"></i>
                    <span className="text-xs sm:text-sm text-gray-700">긴급 구호 지원</span>
                  </div>
                  <div className="flex items-center p-3 bg-amber-50 rounded-xl border border-amber-100">
                    <i className="ri-book-open-line text-amber-600 text-lg mr-2"></i>
                    <span className="text-xs sm:text-sm text-gray-700">신학교 교재 발간</span>
                  </div>
                  <div className="flex items-center p-3 bg-amber-50 rounded-xl border border-amber-100">
                    <i className="ri-more-line text-amber-600 text-lg mr-2"></i>
                    <span className="text-xs sm:text-sm text-gray-700">기타 특별 사역</span>
                  </div>
                </div>
              </div>

              {/* Highlight Box */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 sm:p-5 mb-6 border border-amber-100">
                <div className="flex items-start">
                  <i className="ri-lightbulb-line text-2xl text-amber-500 mr-3 flex-shrink-0"></i>
                  <div>
                    <p className="text-sm sm:text-base text-amber-800 font-medium mb-1">자유로운 후원</p>
                    <p className="text-xs sm:text-sm text-amber-700">
                      언제든지 금액에 상관없이 자유롭게 후원하실 수 있습니다.
                    </p>
                  </div>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-2 sm:space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-double-line text-amber-600 text-lg mt-0.5 mr-2 flex-shrink-0"></i>
                  <span className="text-xs sm:text-sm text-gray-700">자유로운 금액 설정</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-double-line text-amber-600 text-lg mt-0.5 mr-2 flex-shrink-0"></i>
                  <span className="text-xs sm:text-sm text-gray-700">특정 프로젝트 지정 가능</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-double-line text-amber-600 text-lg mt-0.5 mr-2 flex-shrink-0"></i>
                  <span className="text-xs sm:text-sm text-gray-700">기부금 영수증 즉시 발급</span>
                </li>
              </ul>

              {/* CTA Button */}
              <button
                onClick={scrollToForm}
                className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap text-sm sm:text-base"
              >
                <i className="ri-arrow-down-line mr-2"></i>
                일시 후원 신청하기
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Info */}
        <div className="mt-12 sm:mt-16 text-center">
          <p className="text-xs sm:text-sm text-gray-500 mb-4">
            후원 방법에 대해 궁금하신 점이 있으시면 언제든지 문의해주세요.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <a 
              href="tel:010-8524-9200" 
              className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-phone-line text-lg sm:text-xl mr-2"></i>
              <span className="font-medium text-sm sm:text-base">010-8524-9200</span>
            </a>
            <a 
              href="mailto:svkdavid@hanmail.net" 
              className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-mail-line text-lg sm:text-xl mr-2"></i>
              <span className="font-medium text-sm sm:text-base">svkdavid@hanmail.net</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
